// NodeType.js

// Node Types Cache
import { getNodeType, getNodeTypesCache } from 'services/nodetypes/NodeTypesCacheService'; // same cache the Provider uses

// Utilities
import {
  detectNodeTypeFromExtension,
  normalizeFileMimeType,
} from 'utilities/fileMime';
import {
  isClickUpOriginDetailsField,
  isOriginTitleMappedField,
  isSchemaFieldHiddenFromDisplay,
} from 'utilities/schemaFieldVisibility';

/* NODE TYPE SPEC TEMPLATE

  // One document per type in your _nodetypes collection
  
  MOVIE: {
    key: 'MOVIE',                  // Stable internal key (UPPER_SNAKE). Primary lookup id.
    label: 'Movie',                // User-facing singular label.
    labelPlural: 'Movies',          // Optional plural label.
    dataSection: 'movieinfo',      // Where instance data lives on the node document.

    // UI flags (keep these minimal and consistent)
    showHighlight: false,          // Show "highlight" banner/flag in the node page
    autosizeHighlight: false,      // If true, highlight will autosize
    showPeekOnOpen: true,          // When false, openNode goes to Workspace (skip Peek)
    allowTopLevelCreate: true,     // When false, create only from a parent (not from the space)
    showInMenu: true,              // Left-nav Menu (media types with this on go to Media)
    showInAddMenu: true,           // Generic + Add row
    appearAs: [],                   // Named Menu / + Add faces (same type, filter + create defaults)
    parentTypeKey: null,           // Exclusive structural parent type (null = non-exclusive related)
    defaultView: 'LIST',           // Default view for this node type (TABLE, BOARD, GALLERY, LIST, ALPHA_LIST, FEED, CALENDAR, TIMELINE, MAP, FORM)

    // Data section template
    data_movie: {}                 // Node-type specific data (e.g. data_person)

    // Operational switches (optional, but useful)
    version: 2,                    // bump when you curator-edit a type
    // Field schema for the form renderer
    // The following field types are already handled elsewhere and should not be
    // in this list: Title, Event Date, Location, Alternate Title, Description
    schema: {
      fields: [
        // --- Basics ---
        { name: 'releaseDate',     label: 'Release Date',     type: 'date' },
        { name: 'runtimeMinutes',  label: 'Runtime (min)',    type: 'number',  min: 1 },
        { name: 'contentRating',   label: 'Content Rating',   type: 'enum',    options: ['G','PG','PG-13','R','NC-17'] },
        { name: 'director',        label: 'Director',         type: 'string',  default: '' },
        { name: 'officialSite',    label: 'Official Site',    type: 'url' },
        { name: 'trailerUrl',      label: 'Trailer URL',      type: 'url' },
        { name: 'rating',          label: 'Rating',           type: 'number',  min: 0, max: 10, default: 10, decimals: 1 },
        { name: 'boxOfficeUSD',    label: 'Box Office',       type: 'number',  min: 0, unit: 'USD' },
        { name: 'hasPostCreditsScene', label: 'Post-credits Scene', type: 'boolean' },
        { name: 'isSequel',        label: 'Is Sequel',        type: 'boolean' },
      ],
    },

    // Priority sort order for child groups
    prioritySort: ["ACTORS", "DIRECTORS", "REVIEWS"],

    // Suggested child nodes for context-aware “Add Content”
    // Keep it as a *static, denormalized list* produced during transform
    // No need for PHOTO, VIDEO, REVIEW, COMMENT, OR LIVE_ROOM
    suggested: [
      { type: 'ACTOR',  label: 'Actor',  multiple: true },
    ],
}
*/

export const ViewType = {
  TABLE: "TABLE",
  BOARD: "BOARD",
  GALLERY: "GALLERY",
  LIST: "LIST",
  ALPHA_LIST: "ALPHA_LIST",
  FEED: "FEED",
  CALENDAR: "CALENDAR",
  TIMELINE: "TIMELINE",
  SCHEDULER: "SCHEDULER",
  MAP: "MAP",
  FORM: "FORM",
}

const ALPHA_LIST_DEFAULT_TYPE_KEYS = new Set(['CONTACT', 'VENDOR']);

/**
 * Whether a view type should reuse list-item field defaults and choosers.
 *
 * @param {unknown} type
 * @returns {boolean}
 */
export function isListLikeViewType(type) {
  const normalized = String(type || '').trim().toUpperCase();
  return normalized === ViewType.LIST || normalized === ViewType.ALPHA_LIST;
}

/**
 * Directory-style types whose catch-all home view should be ALPHA_LIST
 * when the stored default is unset or the generic LIST.
 *
 * @param {unknown} typeKey
 * @returns {boolean}
 */
export function prefersAlphaListDefaultView(typeKey) {
  return ALPHA_LIST_DEFAULT_TYPE_KEYS.has(String(typeKey || '').trim().toUpperCase());
}

/**
 * Returns the schema fields for a node type.
 *
 * @param {string} typeKey - Node type key to resolve.
 * @param {object} [options]
 * @param {boolean} [options.includeHidden=false] - Whether to include fields flagged as hidden.
 * @param {boolean} [options.excludeSecured=false] - Whether to omit fields flagged as secured.
 * @returns {Array<object>} Ordered array of schema field definitions with source metadata.
 */
export function getResolvedFields(typeKey, { includeHidden = false, excludeSecured = false } = {}) {
  if (!typeKey) return [];

  const cache = getNodeTypesCache();
  if (!cache) return [];

  if (typeof typeKey !== 'string') return [];
  const targetKey = typeKey;
  const def = getNodeType(targetKey);
  if (!def) {
    return [];
  }

  const fields = Array.isArray(def?.schema?.fields) ? def.schema.fields : [];
  return fields
    .filter((field) => {
      if (!field?.name || !field.type) return false;
      if (!includeHidden && isSchemaFieldHiddenFromDisplay(field)) return false;
      if (excludeSecured && field.secured) return false;
      // Origin primary/name → node.title; never render a duplicate Title/Name field.
      if (isOriginTitleMappedField(field, def)) return false;
      // ClickUp assignees → origin details below the form.
      if (isClickUpOriginDetailsField(field, def)) return false;
      return true;
    })
    .map((field) => {
      const copy = { ...field };
      if (!copy.source) copy.source = def.key;
      delete copy.inherited;
      return copy;
    });
}

/**
 * Detects an item type based on the file MIME type and optional filename.
 *
 * Filename is used when MIME is missing, generic (octet-stream), or a vendor
 * PDF alias such as Chrome's application/x-google-chrome-pdf.
 *
 * @param {string} fileType - The MIME type of the file (e.g., "image/jpeg", "application/pdf").
 * @param {string} [fileName] - Optional filename (used for extension fallback).
 *
 * @returns {string} The corresponding item type.
 */
const DOCUMENT_MIME_TYPES = new Set([
  'application/pdf',
  'application/msword',
  'application/vnd.ms-word',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.template',
  'application/vnd.ms-word.document.macroenabled.12',
  'application/vnd.ms-word.template.macroenabled.12',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.template',
  'application/vnd.ms-excel.sheet.macroenabled.12',
  'application/vnd.ms-excel.template.macroenabled.12',
  'application/vnd.ms-excel.sheet.binary.macroenabled.12',
  'application/vnd.ms-excel.addin.macroenabled.12',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.openxmlformats-officedocument.presentationml.template',
  'application/vnd.openxmlformats-officedocument.presentationml.slideshow',
  'application/vnd.ms-powerpoint.presentation.macroenabled.12',
  'application/vnd.ms-powerpoint.slideshow.macroenabled.12',
  'application/vnd.ms-powerpoint.template.macroenabled.12',
  'application/vnd.ms-powerpoint.addin.macroenabled.12',
  'application/vnd.ms-powerpoint.slide.macroenabled.12',
  'application/rtf',
  'application/vnd.oasis.opendocument.text',
  'application/vnd.oasis.opendocument.spreadsheet',
  'application/vnd.oasis.opendocument.presentation',
  'application/vnd.apple.pages',
  'application/vnd.apple.numbers',
  'application/vnd.apple.keynote',
  'application/vnd.google-apps.document',
  'application/vnd.google-apps.spreadsheet',
  'application/vnd.google-apps.presentation',
  'text/plain',
  'text/csv',
  'text/rtf',
  'text/tab-separated-values',
  'text/markdown',
]);

const DOCUMENT_KEYWORDS = [
  'document',
  'word',
  'text',
  'doc',
  'odt',
  'pdf',
  'spreadsheet',
  'sheet',
  'ods',
  'excel',
  'numbers',
  'presentation',
  'slides',
  'powerpoint',
  'ppt',
  'odp',
  'keynote',
  'rtf',
  'csv',
  'tsv',
];

export const detectFileNodeType = (fileType = '', fileName = '') => {
  const lowered = normalizeFileMimeType(fileType, fileName);

  if (lowered.startsWith('image/')) {
    return 'PHOTO';
  }

  if (lowered.startsWith('video/')) {
    return 'VIDEO';
  }

  if (lowered.startsWith('audio/')) {
    return 'GALLERY'; // fallback until dedicated audio type exists
  }

  if (DOCUMENT_MIME_TYPES.has(lowered)) {
    return 'DOCUMENT';
  }

  if (lowered.startsWith('application/') || lowered.startsWith('text/')) {
    if (DOCUMENT_KEYWORDS.some((keyword) => lowered.includes(keyword))) {
      return 'DOCUMENT';
    }
  }

  if (
    lowered.includes('zip') ||
    lowered.includes('compressed') ||
    lowered.includes('tar') ||
    lowered.includes('7z') ||
    lowered.includes('rar') ||
    lowered.includes('gzip')
  ) {
    return 'FILE';
  }

  // Empty / unknown MIME: trust the filename extension (Chrome PDF, Finder drops, etc.)
  const fromExtension = detectNodeTypeFromExtension(fileName);
  if (fromExtension) return fromExtension;

  return 'FILE';
};



